import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input, RadioGroup, Checkbox } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReverseChargeDocumentFilePicker } from '../controls/reverse-charge-document-file-picker';
import { LegalIdentifierNumberField } from '../controls/legal-identitfier-number-field';
import { TaxCertificateFilePicker } from '../controls';
import { TaxTypeFormPropTypes } from '../shapes';
import {
  getHelpPopupTopicFromTaxSchema,
  shouldDisplayTaxCertifications,
  onTaxFormValidateFillingLegalIdentifier,
  addTaxApprovalFormInTaxSchema,
  isTaxExemptionCertVerified,
} from '../utils';
import { InputTaxFieldId, LegalIdentifierNumberPilot, IsTaxExemptionCertVerifiedTypeKey, IsTaxExemptionCertVerifiedLabelKey } from '../constants';
import { getTaxFormsStyles } from '../styles';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  TaxType, ReverseChargeDocumentAttestationValueToLabel, AttestationValues, TaxInfo, TaxExemptionCertVerifiedResult,
} = Constants;
const { normalizeAttestationValue, getTaxCertLabelDisplay } = TaxUtils;

const initTaxData = data => ({
  ...data,
  [TaxInfo.ReverseChargeDocumentAttestation]: normalizeAttestationValue(data[TaxInfo.ReverseChargeDocumentAttestation]),
});

const checkboxConvertor = {
  fromModel: isTaxExemptionCertVerified,
  toModel: value => (value ? TaxExemptionCertVerifiedResult.Accept : TaxExemptionCertVerifiedResult.Reject),
};

function getTaxSchema(taxSchemaGetter, { showTaxApprovalForm, ...options }) {
  const formSchema = taxSchemaGetter(options);
  if (showTaxApprovalForm) {
    addTaxApprovalFormInTaxSchema(formSchema);
    return formSchema;
  }
  return formSchema;
}

export const GlobalTaxForm = ({
  i18n,
  onValidate,
  taxSchemaGetter,
  vatLabel,
  customStrings,
  vatValidatorErrorMessage,
  showLineByLineFormGroup,
  alwaysShowError,
  data,
  mtData,
  taxCertification,
  mtTaxCertification,
  urlBuilder,
  permissions,
  disabled,
  taxIdValidator,
  taxCertLabel,
  showTaxCert,
  showReverseDocument,
  taxCertScenario,
  taxCertOptional,
  showLegalIdentifierNumberField,
  legalIdentifierHint,
  showTaxApprovalForm,
}) => {
  const validationI18n = useValidationI18n(i18n);
  const initialTaxData = useMemo(() => initTaxData(data), [data]);
  const [taxDataState, setTaxDataState] = useState({
    data: initialTaxData,
    isValid: false,
  });
  const [certificationsState, setCertificationsState] = useState({
    certifications: [],
    isValid: false,
  });
  const [reverseChargeDocumentsState, setReverseDocumentsState] = useState({
    documents: [],
    isValid: false,
  });
  const classes = useDefaultStyles(getTaxFormsStyles);

  const showAttestation = useMemo(() => !_.isEmpty(reverseChargeDocumentsState.documents) || !_.isEmpty(mtTaxCertification.reverseChargeDocuments), [mtTaxCertification.reverseChargeDocuments, reverseChargeDocumentsState.documents]);
  const taxSchema = getTaxSchema(taxSchemaGetter, { permissions, isReverseChargeDocumentAttestationRequired: showAttestation, showTaxApprovalForm });
  const additionalFormData = useMemo(() => ({ scenario: taxCertScenario }), [taxCertScenario]);
  const { taxCertifications: prefilledTaxCertifications, reverseChargeDocuments: prefilledReverseChargeDocuments } = taxCertification;
  const prefilledDisplayCertifications = shouldDisplayTaxCertifications(prefilledTaxCertifications) ? prefilledTaxCertifications : [];
  const prefilledDisplayReverseChargeDocuments = shouldDisplayTaxCertifications(prefilledReverseChargeDocuments) ? prefilledReverseChargeDocuments : [];
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const hasVatNumber = !_.isEmpty(taxDataState.data.VatNumber);

  const isTaxDataRequired = !_.isEmpty(certificationsState.certifications);
  const isCertRequired = !taxCertOptional && showTaxCert && hasVatNumber && (taxDataState.data.VatNumber !== mtData.VatNumber || _.isEmpty(mtTaxCertification.taxCertifications));
  const taxCertLabelDisplay = getTaxCertLabelDisplay(taxCertLabel, taxCertOptional, i18n);
  const isVatTaxDataValid = taxIdValidator(taxDataState.data.VatNumber) || (!isTaxDataRequired && _.isEmpty(taxDataState.data.VatNumber));
  const IsLegalIdentifierNumberPilotOn = _.chain(permissions).result('dynamic').result(LegalIdentifierNumberPilot).value();
  const shouldShowLegalIdentifierNumberField = showLegalIdentifierNumberField && IsLegalIdentifierNumberPilotOn;

  const onTaxFormValidate = useCallback(({ data: taxData, isValid }) => (
    shouldShowLegalIdentifierNumberField
      ? onTaxFormValidateFillingLegalIdentifier({
        newTaxData: {
          data: taxData,
          isValid,
        },
        onValidate: setTaxDataState,
        prevTaxData: taxDataState.data,
        listenedToField: TaxType.VatNumber,
      })
      : setTaxDataState({
        data: taxData,
        isValid,
      })
  ), [shouldShowLegalIdentifierNumberField, taxDataState.data]);

  const onCertificationsChange = useCallback(({ certifications, isValid }) => {
    setCertificationsState({
      certifications,
      isValid,
    });
  }, []);

  const onDocumentsChange = useCallback(({ documents, isValid }) => {
    setReverseDocumentsState({ documents, isValid });
  }, []);

  useEffect(() => {
    const taxCertifications = _.isEmpty(certificationsState.certifications) ? mtTaxCertification.taxCertifications : certificationsState.certifications;
    const reverseChargeDocuments = _.isEmpty(reverseChargeDocumentsState.documents) ? mtTaxCertification.reverseChargeDocuments : reverseChargeDocumentsState.documents;
    const isCertSuccessfullyUploaded = _.every(taxCertifications, cert => cert && cert.guid);
    const isReverseChargeDocumentScccessfullyUploaded = _.every(reverseChargeDocuments, cert => cert && cert.guid);
    const isReverseChargeDocumentValid = !showReverseDocument || (reverseChargeDocumentsState.isValid && isReverseChargeDocumentScccessfullyUploaded);
    const isCertValid = !showTaxCert || (certificationsState.isValid && isCertSuccessfullyUploaded);
    const isValid = taxDataState.isValid && isVatTaxDataValid && isCertValid && isReverseChargeDocumentValid;

    onValidate({
      data: taxDataState.data,
      taxCertification: {
        taxCertifications,
        reverseChargeDocuments,
      },
      isValid,
    });
  }, [certificationsState, reverseChargeDocumentsState, mtTaxCertification.taxCertifications, mtTaxCertification.reverseChargeDocuments, showReverseDocument, isVatTaxDataValid, onValidate, taxDataState.isValid, taxDataState.data, showTaxCert]);

  // Clear all data when unmount
  // useLayoutEffect to make sure it runs before the next component's effect
  useLayoutEffect(() => () => onValidate({
    data: {},
    taxCertification: null,
    isValid: true,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <div className="global-tax-form">
      <ValidationGroup
        validationProps={{
          schema: taxSchema,
          i18n: validationI18n,
          onValidate: onTaxFormValidate,
          data: initialTaxData,
        }}
      >
        <CustomizedFormGroup
          isStatic
          label={vatLabel}
          helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.VatNumber })}
        >
          <Input
            id={InputTaxFieldId(TaxType.VatNumber)}
            validationProps={{
              field: TaxType.VatNumber,
              alwaysShowError,
              showErrorOnPassiveMessages: true,
              passiveMessages: isVatTaxDataValid
                ? []
                : [hasVatNumber ? vatValidatorErrorMessage : i18n.getString(_TL_('This field is required'))],
            }}
            disabled={disabled}
          />
        </CustomizedFormGroup>
        {showTaxCert &&
          <TaxCertificateFilePicker
            i18n={i18n}
            customStrings={customStrings}
            certifications={prefilledDisplayCertifications}
            mtTaxCertification={mtTaxCertification}
            onCertificationsChange={onCertificationsChange}
            showLineByLineFormGroup={showLineByLineFormGroup}
            alwaysShowError={alwaysShowError || hasVatNumber}
            disabled={disabled}
            required={isCertRequired}
            urlBuilder={urlBuilder}
            taxCertLabel={taxCertLabelDisplay}
            additionalFormData={additionalFormData}
            isGlobalTaxForm
          />
        }
        {showReverseDocument &&
          <ReverseChargeDocumentFilePicker
            i18n={i18n}
            documents={prefilledDisplayReverseChargeDocuments}
            mtTaxCertification={mtTaxCertification}
            onDocumentsChange={onDocumentsChange}
            showLineByLineFormGroup={showLineByLineFormGroup}
            alwaysShowError={alwaysShowError}
            urlBuilder={urlBuilder}
          />}
        {showAttestation &&
          <CustomizedFormGroup
            isStatic
            label={i18n.getString(_TL_("Please attest that the above Reverse Charge Document has been uploaded by an employee of the company that is receiving advertising services from Microsoft Advertising, and that the uploader understands the reverse charge account concept and the company's VAT return filing obligations."))}
            className="reverse-charge-document-attestation"
          >
            <div className={classes.attestationRadios}>
              <RadioGroup
                i18n={i18n}
                items={AttestationValues}
                renderItem={itemValue => i18n.getString(ReverseChargeDocumentAttestationValueToLabel[itemValue])}
                name="ReverseChargeDocumentsAttestationRadioGroup"
                validationProps={{ i18n: validationI18n, field: 'ReverseChargeDocumentAttestation', alwaysShowError }}
              />
            </div>
          </CustomizedFormGroup>}
        {shouldShowLegalIdentifierNumberField &&
          <LegalIdentifierNumberField
            i18n={i18n}
            hintContent={legalIdentifierHint}
            showLineByLineFormGroup
            alwaysShowError={alwaysShowError}
            disabled={disabled}
          />}
        {showTaxApprovalForm && (
          <Checkbox
            id={`tax-checkbox-${IsTaxExemptionCertVerifiedTypeKey}`}
            validationProps={{ field: IsTaxExemptionCertVerifiedTypeKey, convertor: checkboxConvertor }}
            disabled={disabled}
          >
            {i18n.getString(IsTaxExemptionCertVerifiedLabelKey)}
          </Checkbox>
        )}
      </ValidationGroup>
    </div>
  );
};

GlobalTaxForm.propTypes = {
  ...TaxTypeFormPropTypes,
  vatLabel: PropTypes.string.isRequired,
  vatValidatorErrorMessage: PropTypes.string.isRequired,
  taxSchemaGetter: PropTypes.func.isRequired,
  customStrings: PropTypes.shape({}),
  taxCertLabel: PropTypes.string,
  taxIdValidator: PropTypes.func.isRequired,
  taxCertScenario: PropTypes.string,
  showTaxCert: PropTypes.bool,
  showReverseDocument: PropTypes.bool,
  taxCertOptional: PropTypes.bool,
  showLegalIdentifierNumberField: PropTypes.bool,
  legalIdentifierHint: PropTypes.string,
  showTaxApprovalForm: PropTypes.bool,
};

GlobalTaxForm.defaultProps = {
  taxCertLabel: null,
  customStrings: {},
  showTaxCert: true,
  showReverseDocument: false,
  taxCertOptional: false,
  showLegalIdentifierNumberField: false,
  legalIdentifierHint: undefined,
  taxCertScenario: '',
  showTaxApprovalForm: false,
};
